import { css } from '@emotion/css';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import QuestionnaireCard from 'atoms/QuestionnaireCard';
import Skeleton from 'atoms/Skeleton';
import ErrorSurveyModal from 'modals/ErrorSurveyModal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadingEnd, loadingStart } from 'store/globalSlice';
import { GroupedQuestionnaires, setOpenedErrorSurveyModal, setQuestionnaries } from 'store/questionnaireSlice';
import { RootState } from 'store/store';
import { setParticipantDetails } from 'store/userSlice';
import { colors } from 'styles/colors';
import { ParticipantAPI } from 'utils/ParticipantAPI';
import { QuestionnaireAPI, QuestionnaireStatus } from 'utils/QuestionnaireAPI';

const QuestionnariesList = () => {
    const dispatch = useDispatch();
    const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>({});

    const token = useSelector((state: RootState) => state.global.authToken);
    const questionnaries = useSelector((state: RootState) => state.questionaire.questionnaries);
    // const participantDetails = useSelector((state: RootState) => state.user.participantDetails);
    const openedErrorSurveyModal = useSelector((state: RootState) => state.questionaire.openedErrorSurveyModal);
    const isLoading = useSelector((state: RootState) => state.global.isLoading);

    const getQuestionnaires = useCallback(async () => {
        try {
            dispatch(loadingStart());
            const questionnaires = await QuestionnaireAPI.getQuestionnairesResponse(token ?? '');
            dispatch(setQuestionnaries(questionnaires.questionnaireResponses));
        } catch (e) {
            console.error(e);
        }
        dispatch(loadingEnd());
    }, [dispatch, token]);

    const filteredAndGroupedUncompletedQuestionnaires = useMemo(() => {
        if (questionnaries !== null) {
            const base = questionnaries.filter((q: QuestionnaireStatus) => !q.isCompleted);

            const grouped = base.reduce((group: GroupedQuestionnaires, questionnaire: QuestionnaireStatus) => {
                const groupName = questionnaire.groupName || 'null';
                if (!group[groupName]) {
                    group[groupName] = [];
                }
                group[groupName].push(questionnaire);
                return group;
            }, {} as GroupedQuestionnaires);

            Object.keys(grouped).forEach((groupName) => {
                grouped[groupName] = grouped[groupName].sort((a: any, b: any) => a.name.localeCompare(b.name));
            });

            const sortedGroups = Object.keys(grouped)
                .sort((a, b) => (a === 'null' ? 1 : b === 'null' ? -1 : a.localeCompare(b)))
                .reduce((group: GroupedQuestionnaires, groupName: string) => {
                    group[groupName] = grouped[groupName];
                    return group;
                }, {} as GroupedQuestionnaires);

            return sortedGroups;
        }
        return {} as GroupedQuestionnaires;
    }, [questionnaries]);

    const toggleGroup = (groupName: string) => {
        setExpandedGroups((prev: { [key: string]: boolean }) => ({
            ...prev,
            [groupName]: !prev[groupName],
        }));
    };

    const filteredAndSortedCompletedQuestionnaries = useMemo(() => {
        if (questionnaries !== null) {
            return questionnaries
                .filter((q) => q.isCompleted && q.completedDate !== null)
                .sort((a, b) => {
                    const dateA = questionnaireDateValue(a.completedDate);
                    const dateB = questionnaireDateValue(b.completedDate);
                    return dateB.getTime() - dateA.getTime();
                });
        }
        return [] as QuestionnaireStatus[];
    }, [questionnaries]);

    function questionnaireDateValue(value: string | null): Date {
        return value ? new Date(value) : new Date(0);
    }

    const getDetails = useCallback(async () => {
        try {
            const participantDetailsResponse = await ParticipantAPI.getParticipantDetails(token ?? '');
            dispatch(setParticipantDetails(participantDetailsResponse));
        } catch (e) {
            console.error(e);
        }
    }, [dispatch, token]);

    const incompletedList = useMemo((): boolean => {
        if (questionnaries !== null) {
            return questionnaries.every((questionnaire: QuestionnaireStatus) => questionnaire.isCompleted === false);
        }
        return true;
    }, [questionnaries]);

    useEffect(() => {
        getQuestionnaires();
        getDetails();
    }, [getDetails, getQuestionnaires]);

    // Collapse all groups by default when questionnaires change
    useEffect(() => {
        if (questionnaries) {
            const uncompleted = questionnaries.filter((q: QuestionnaireStatus) => !q.isCompleted);
            const groups = uncompleted.reduce((acc: Record<string, boolean>, q) => {
                const name = q.groupName || 'null';
                acc[name] = false; // collapsed by default
                return acc;
            }, {} as Record<string, boolean>);
            setExpandedGroups(groups);
        }
    }, [questionnaries]);

    return (
        <div className={styles.content}>
            <div className={styles.textContainer}>
                <Skeleton loading={isLoading}>
                    <div className={styles.title}>Spørgeskemaer</div>
                </Skeleton>
                <Skeleton loading={isLoading}>
                    <div className={styles.underTitleText}>
                        For at opnå den bedst mulige spørgeskemabesvarelse er det vigtigt, at du er ærlig og nøjagtigt i
                        dine svar. Evaluer hvert spørgsmål individuelt, og forsøg at undgå at sammenligne med tidligere
                        spørgsmål. Du kan med fordel bruge det anslåede tidsforbrug angivet under hvert spørgeskema til
                        at overveje, hvornår på dagen eller i løbet af ugen du besvarer det. <br></br>
                        Husk at du tidligst skal starte på hvert set spørgskemaer 7 dage inden dit kommende klinikbesøg.{' '}
                        <br></br>
                    </div>
                </Skeleton>
            </div>
            <div className={styles.cardsConteiner}>
                <Box sx={{ flexGrow: 1 }}>
                    <Grid
                        container
                        columns={2}
                        columnSpacing={10}
                        justifyContent="center"
                        alignItems="flex-start"
                        wrap="wrap"
                    >
                        <Grid item xs={2} sm={2} md={1} lg={1}>
                            <div>
                                <Skeleton loading={isLoading}>
                                    <div className={styles.titleLists}>Ubesvarede spørgeskemaer</div>
                                </Skeleton>

                                <div className={styles.scrollArea}>
                                    {isLoading
                                        ? ([{}, {}, {}] as QuestionnaireStatus[]).map((questionnaire, index) => (
                                            <div key={index} className={styles.forPadding}>
                                                <QuestionnaireCard {...questionnaire} />
                                            </div>
                                        ))
                                        : Object.keys(filteredAndGroupedUncompletedQuestionnaires).map((groupName) => {
                                            const questionnaires =
                                                  filteredAndGroupedUncompletedQuestionnaires[groupName];
                                            const isExpanded = expandedGroups[groupName];
                                            const displayedQuestionnaires = isExpanded
                                                ? questionnaires
                                                : questionnaires.slice(0, 2);
                                            const groupId = `group-${groupName.replace(/\s+/g, '-')}`;

                                            return (
                                                <div
                                                    key={groupName}
                                                    style={groupName === 'null' ? { paddingTop: '30px' } : {}}
                                                >
                                                    {groupName !== 'null' && (
                                                        <div className={styles.groupTitle}>{groupName}</div>
                                                    )}

                                                    <div id={groupId}>
                                                        {displayedQuestionnaires.map(
                                                            (questionnaire: QuestionnaireStatus, key: any) => (
                                                                <div key={key} className={styles.forPadding}>
                                                                    <QuestionnaireCard {...questionnaire} />
                                                                </div>
                                                            )
                                                        )}
                                                    </div>

                                                    {questionnaires.length > 2 && (
                                                        <div
                                                            onClick={() => toggleGroup(groupName)}
                                                            className={styles.toggleText}
                                                        >
                                                            {isExpanded ? 'Se færre' : 'Se flere'}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={2} sm={2} md={1} lg={1}>
                            <div>
                                <Skeleton loading={isLoading}>
                                    <div className={styles.titleLists}>Besvarede spørgeskemaer</div>
                                </Skeleton>

                                {!isLoading && incompletedList ? (
                                    <div className={styles.emptyPassedList}>
                                        Du har ikke besvaret spørgeskemaer endnu.
                                    </div>
                                ) : (
                                    <div className={styles.scrollArea}>
                                        {isLoading
                                            ? ([{}, {}, {}] as QuestionnaireStatus[]).map((questionnaire, index) => (
                                                <div key={index} className={styles.forPadding}>
                                                    <QuestionnaireCard {...questionnaire} />
                                                </div>
                                            ))
                                            : filteredAndSortedCompletedQuestionnaries !== null &&
                                              filteredAndSortedCompletedQuestionnaries.map((questionnaire, key) => {
                                                  if (questionnaire.isCompleted === true) {
                                                      return (
                                                          <div key={key} className={styles.forPadding}>
                                                              <QuestionnaireCard {...questionnaire} />
                                                          </div>
                                                      );
                                                  }

                                                  return null;
                                              })}
                                    </div>
                                )}
                            </div>
                        </Grid>
                    </Grid>
                </Box>
                <ErrorSurveyModal
                    open={openedErrorSurveyModal}
                    onClose={() => {
                        dispatch(setOpenedErrorSurveyModal(false));
                    }}
                    text={'Spørgeskemaer kan kun besvares til og med dagen efter testdag 4'}
                />
            </div>
        </div>
    );
};

const styles = {
    content: css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '7rem',
        paddingRight: '20px',
        paddingLeft: '20px',
    }),
    textContainer: css({
        justifyContent: 'flex-start',
        width: '100%',
        marginBottom: '2rem',
    }),
    title: css({
        color: colors.primaryText,
        fontFamily: 'Figtree',
        fontSize: '35px',
        fontWeight: 'bold',
        lineHeight: 'normal',
        paddingBottom: '1rem',
    }),
    underTitleText: css({
        color: colors.primaryText,
        fontFamily: 'Figtree',
        fontSize: '22px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '150%',
        letterSpacing: '0.0275rem',
        maxWidth: '80rem',
    }),
    cardsConteiner: css({
        display: 'flex',
        width: '100%',
        justifyContent: 'center', // center the Grid block
    }),
    titleLists: css({
        color: colors.primaryText,
        fontFamily: 'Figtree',
        fontSize: '22px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: 'normal',
        paddingBottom: '2rem',
    }),
    emptyPassedList: css({
        color: colors.primaryText,
        fontFamily: 'Figtree',
        fontSize: '22px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '2.0625rem',
        letterSpacing: '0.0275rem',
    }),
    forPadding: css({
        paddingRight: '0px',
        '@media (max-width: 1000px) and (min-width: 600px)': {
            paddingRight: '10px',
        },
        '@media (max-width: 488px)': {
            paddingRight: '10px',
        },
    }),
    groupTitle: css({
        color: colors.primaryText,
        fontSize: '18px',
        fontWeight: 'bold',
        paddingBottom: '1rem',
        fontFamily: 'Figtree',
    }),
    toggleText: css({
        color: colors.primaryText,
        fontSize: '16px',
        cursor: 'pointer',
        textDecoration: 'underline',
        paddingBottom: '1rem',
        justifyContent: 'center',
        display: 'flex',
        fontFamily: 'Figtree',
    }),
    scrollArea: css({
        maxHeight: '450px',
        maxWidth: '27.5rem',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        paddingRight: '1rem',
        scrollbarGutter: 'stable',
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': { background: 'transparent', borderRadius: '8px' },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#D0D0D0',
            borderRadius: '8px',
            border: '2px solid transparent',
            backgroundClip: 'content-box',
        },
        '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a1a1a1' },
        scrollbarWidth: 'thin',
        scrollbarColor: '#D0D0D0 transparent',
    }),
};

export default QuestionnariesList;
